import { createSlice } from "@reduxjs/toolkit";
import { onSetError, onSetLoaded, onSetLoadedUpdate, onErrorUpdate } from "../utils/loadedError";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

export const detectConflicts = (localData, serverData) => {
  const localSet = new Set(localData.active_ids);
  const serverSet = new Set(serverData.active_ids);
  let isConflict = false;
  const conflicts = {
    serverAddedLocalRemoved: [],
    serverRemovedLocalAdded: [],
  };

  for (const id of serverSet) {
    if (!localSet.has(id) && localData.removed_ids?.includes(id)) {
      conflicts.serverAddedLocalRemoved.push(id);
      isConflict = true;
    }
  }

  for (const id of localSet) {
    if (!serverSet.has(id) && localData.added_ids?.includes(id)) {
      conflicts.serverRemovedLocalAdded.push(id);
      isConflict = true;
    }
  }

  return { conflicts, isConflict };
};

const initialgeneralsState = {
  data: [],
  changedData: [],
  ids: [],
  Error: null,
  isLoaded: false,
  isLoadedUpdate: true,
  errorUpdate: null,
  changedIds: [],
};

const generalsSlice = createSlice({
  name: "generals",
  initialState: initialgeneralsState,
  reducers: {
    onInitialGenerals(state, action) {
      state.data = cloneDeep(action.payload);
      state.changedData = cloneDeep(action.payload);
      state.isLoaded = true;
      state.Error = null;
      state.isLoadedUpdate = true;
      state.errorUpdate = null;
      state.changedIds = [];
    },

    onChangeGeneralsActive(state, action) {
      let { portal: portal_id, wellId, isActive } = action.payload;
      let isReallyChanged = false;

      state.changedData = state.changedData.map((portal) => {
        if (portal.portal_id === portal_id) {
          let newActiveIds = [...portal.active_ids];
          if (isActive && !newActiveIds.includes(wellId)) {
            newActiveIds.push(wellId);
          }
          if (!isActive) {
            newActiveIds = newActiveIds.filter((item) => item !== wellId);
          }
          isReallyChanged = !isEqual(newActiveIds, portal.active_ids);
          return {
            ...portal,
            active_ids: newActiveIds,
          };
        } else {
          return portal;
        }
      });

      if (isReallyChanged) {
        const index = state.changedIds.indexOf(portal_id);
        if (isReallyChanged && index === -1) {
          state.changedIds.push(portal_id);
        } else if (index !== -1) {
          state.changedIds.splice(index, 1);
        }
      }
    },

    setExternalUpdateGenerals(state, action) {
      const { serverData, diff, force } = action.payload;
      const portalId = serverData.portalId;

      const dataIndex = state.data.findIndex((p) => p._id === portalId || p.id === portalId);
      if (dataIndex === -1) return;

      const initData = state.data[dataIndex];
      state.data[dataIndex] = { ...initData, ...serverData };

      const changedIndex = state.changedData.findIndex((p) => p._id === portalId || p.id === portalId);
      if (changedIndex === -1) return;

      if (Boolean(diff)) {
        const mergedActive = new Set(initData.active_ids);
        diff.add.forEach((id) => mergedActive.add(id));
        serverData.diff.add.forEach((id) => mergedActive.add(id));
        diff.remove.forEach((id) => mergedActive.delete(id));
        serverData.diff.remove.forEach((id) => mergedActive.delete(id));

        state.changedData[changedIndex].active_ids = Array.from(mergedActive);

        if (serverData.version !== undefined) {
          state.changedData[changedIndex].version = serverData.version;
        }
      } else {
        state.changedData[changedIndex] = { ...state.changedData[changedIndex], ...serverData };
      }
    },

    onSetError,
    onSetLoaded,
    onSetLoadedUpdate,
    onErrorUpdate,
    onClearStore(state) {
      state = initialgeneralsState;
    },

    updateGenerals(state, action) {
      const { diff, userId, ...rest } = action.payload;
      state.data = state.data.map((item) => (item.id === rest.id ? rest : item));
      state.changedData = state.changedData.map((item) => (item.id === rest.id ? rest : item));
      state.changedIds = state.changedIds.filter((id) => id !== rest.id);
    },
    updateDataSuccess(state, action) {
      state.data = state.data.map((item) => (item.id === action.payload.id ? action.payload : item));
      state.changedData = state.changedData.map((item) => (item.id === action.payload.id ? action.payload : item));
      state.changedIds = [];
    },
  },
});

export const checkIsActiveWellbore = (generals, wellbore_id, portal) => {
  if (!portal) return false;
  let portalIds = generals.find((item) => item.portal_id === portal)?.active_ids || [];
  return portalIds.includes(wellbore_id);
};

export const generalsActions = generalsSlice.actions;
export default generalsSlice.reducer;
